import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIcon } from '@angular/material/icon';
import { TestStepService } from '../../../services/test-step.service';
import { TestStepActivity, TestStepActivityType } from '../../../models/test-case.model';

/** Material-Icon je Aktivitätstyp eines Bibliotheks-Schritts. */
const ICON_BY_TYPE: Record<TestStepActivityType, string> = {
  CREATED: 'add',
  EDITED: 'edit',
};

const TITLE_KEY_PREFIX = 'testSteps.form.activity.types.';

/** Aufbereiteter Feed-Eintrag fürs Template. */
interface ActivityItem {
  readonly id: string;
  readonly icon: string;
  readonly titleKey: string;
  readonly createdByName: string | null;
  readonly createdAt: string;
}

/**
 * Aktivitäts-Feed eines Bibliotheks-Schritts (NEX-459) als kleine vertikale
 * Timeline: Icon-Badge je Typ (CREATED/EDITED), übersetzter Titel, Autor und
 * Zeitstempel. Lädt aus {@code GET /test-steps/{id}/activity}. Bewusst ohne
 * eigenen Karten-Rahmen — wird in die "Details & Historie"-Karte eingebettet.
 */
@Component({
  selector: 'app-test-step-activity-feed',
  imports: [TranslatePipe, DatePipe, MatProgressSpinner, MatIcon],
  templateUrl: './test-step-activity-feed.component.html',
  styleUrl: './test-step-activity-feed.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestStepActivityFeedComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly testStepService = inject(TestStepService);

  readonly projectId = input.required<string>();
  readonly stepId = input<string | null>(null);

  readonly loading = signal(false);
  readonly loadError = signal(false);
  private readonly activities = signal<TestStepActivity[]>([]);

  readonly items = computed<ActivityItem[]>(() =>
    this.activities().map((a) => ({
      id: a.id,
      icon: ICON_BY_TYPE[a.type] ?? 'edit',
      titleKey: `${TITLE_KEY_PREFIX}${a.type}`,
      createdByName: a.createdByName ?? null,
      createdAt: a.createdAt,
    })),
  );

  ngOnInit(): void {
    this.reload();
  }

  /** Lädt den Feed neu (z. B. nach einem Save, das einen EDITED-Eintrag erzeugt). */
  reload(): void {
    const id = this.stepId();
    if (!id) return;
    this.loading.set(true);
    this.loadError.set(false);
    this.testStepService
      .listActivity(this.projectId(), id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (activities) => {
          this.activities.set(activities);
          this.loading.set(false);
        },
        error: () => {
          this.loadError.set(true);
          this.loading.set(false);
        },
      });
  }
}
